import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../lib/styleGuide';
import '../index.css';

// Zod schema for signup validation
const signupSchema = z.object({
  displayName: z.string()
    .min(2, 'Full name must be at least 2 characters')
    .max(50, 'Full name must be less than 50 characters')
    .regex(/^[a-zA-Z\s\-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes'),
  email: z.string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters long')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
});

const EyeIcon = ({ open }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-5 h-5"
    style={{ color: '#757575' }}
  >
    {open ? (
      <React.Fragment>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12s-3.75 6.75-9.75 6.75S2.25 12 2.25 12z" />
        <circle cx="12" cy="12" r="3" stroke="#757575" strokeWidth="1.5" fill="none" />
      </React.Fragment>
    ) : (
      <React.Fragment>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 3l18 18M4.5 4.5C2.5 7 2.5 12 2.5 12s3.75 6.75 9.75 6.75c2.1 0 4.05-.45 5.75-1.25M19.5 19.5C21.5 17 21.5 12 21.5 12s-3.75-6.75-9.75-6.75c-2.1 0-4.05.45-5.75 1.25"
        />
      </React.Fragment>
    )}
  </svg>
);

const SignUp = ({ onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(signupSchema),
    mode: 'onChange', // Real-time validation
  });

  // Watch password for strength calculation
  const password = watch('password', '');

  const onSubmit = async (data) => {
    try {
      setError('');
      setLoading(true);
      await signup(data.email, data.password, data.displayName);
      
      // Handle redirect or callback
      const returnTo = searchParams.get('returnTo');
      if (onSuccess) {
        onSuccess();
      } else if (returnTo) {
        navigate(decodeURIComponent(returnTo), { replace: true });
      } else {
        navigate('/fooddiscovery');
      }
    } catch (error) {
      setError('Failed to create account: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      
      // Handle redirect or callback
      const returnTo = searchParams.get('returnTo');
      if (onSuccess) {
        onSuccess();
      } else if (returnTo) {
        navigate(decodeURIComponent(returnTo), { replace: true });
      } else {
        navigate('/fooddiscovery');
      }
    } catch (error) {
      setError('Failed to sign up with Google: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getPasswordStrength = () => {
    if (password.length === 0) return { strength: 0, color: 'gray', text: '' };
    if (password.length < 8) return { strength: 1, color: 'red', text: 'Too short' };
    
    // Check for all requirements
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    
    const requirements = [hasUpperCase, hasLowerCase, hasNumber, hasSpecial];
    const metRequirements = requirements.filter(Boolean).length;
    
    if (metRequirements === 4) return { strength: 3, color: 'green', text: 'Strong' };
    if (metRequirements >= 2) return { strength: 2, color: 'yellow', text: 'Medium' };
    return { strength: 1, color: 'red', text: 'Weak' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="w-full max-w-md mx-auto py-12 px-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
      {/* Header */}
      <h1 
        className="text-4xl font-bold mb-2 text-left" 
        style={{
          fontWeight: 700,
          background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        Join
      </h1>
      <p className="text-base text-[#757575] mb-10 text-left" style={{fontWeight: 400}}>Create a new account</p>
      
      {/* Form */}
      <form className="space-y-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-2 text-center">
            {error}
          </div>
        )}
        
        {/* Full Name */}
        <div className="w-full">
          <label htmlFor="display-name" className="block text-sm font-semibold text-black mb-2 text-left" style={{fontWeight: 600}}>
            Full Name
          </label>
          <input
            id="display-name"
            {...register('displayName')}
            type="text"
            autoComplete="name"
            required
            className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent transition-all duration-200 text-base placeholder-[#bdbdbd] ${
              errors.displayName ? 'border-red-300 bg-red-50' : ''
            }`}
            placeholder="Enter your full name"
          />
          {errors.displayName && (
            <p className="mt-2 text-sm text-red-600">{errors.displayName.message}</p>
          )}
        </div>
        
        {/* Email */}
        <div className="w-full">
          <label htmlFor="email-address" className="block text-sm font-semibold text-black mb-2 text-left" style={{fontWeight: 600}}>
            Email Address
          </label>
          <input
            id="email-address"
            {...register('email')}
            type="email"
            autoComplete="email"
            required
            className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent transition-all duration-200 text-base placeholder-[#bdbdbd] ${
              errors.email ? 'border-red-300 bg-red-50' : ''
            }`}
            placeholder="Enter your email"
          />
          {errors.email && (
            <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>
        
        {/* Password */}
        <div className="w-full">
          <label htmlFor="password" className="block text-sm font-semibold text-black mb-2 text-left" style={{fontWeight: 600}}>
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              {...register('password')}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              className={`w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C97B53] focus:border-transparent transition-all duration-200 text-base placeholder-[#bdbdbd] ${
                errors.password ? 'border-red-300 bg-red-50' : ''
              }`}
              placeholder="Create a password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-3 flex items-center px-2"
              onClick={togglePasswordVisibility}
              tabIndex={-1}
              style={{height: '100%', background: 'none', border: 'none', cursor: 'pointer'}}
            >
              <EyeIcon open={showPassword} />
            </button>
          </div>
          
          {password && (
            <div className="mt-3">
              <div className="flex items-center space-x-3">
                <div className="flex space-x-1">
                  {[1, 2, 3].map((level) => (
                    <div
                      key={level}
                      className={`h-2 w-12 rounded-full transition-all duration-200 ${
                        passwordStrength.strength >= level ? `bg-${passwordStrength.color}-500` : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <span className={`text-sm font-medium text-${passwordStrength.color}-600`}>
                  {passwordStrength.text}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Password must be at least 8 characters long with uppercase, lowercase, number, and special character
              </p>
            </div>
          )}
          
          {errors.password && (
            <p className="mt-2 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>
        
        {/* Terms */}
        <div className="w-full text-xs text-[#757575] text-left mb-2">
          By creating an account, you agree to our{' '}
          <Link to="/terms" style={{ color: '#c97b53', fontWeight: 500 }}>
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" style={{ color: '#c97b53', fontWeight: 500 }}>
            Privacy Policy
          </Link>.
        </div>
        
        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || !isValid}
          className="w-full mt-2 px-4 py-3 border border-transparent text-base font-medium rounded-md text-white transition-opacity"
          style={{
            backgroundColor: !isValid ? '#D1D5DB' : COLORS.PRIMARY_BROWN,
            fontWeight: 600,
            fontSize: '1.125rem',
            borderRadius: '12px',
            cursor: !isValid ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Creating account...' : 'Create Account'}
        </button>
      </form>
      
      {/* OR Divider */}
      <div className="flex justify-center items-center my-8 w-full">
        <div className="flex-grow border-t border-gray-200" />
        <span className="mx-3 text-[#757575] font-medium text-base" style={{fontWeight: 500}}>OR</span>
        <div className="flex-grow border-t border-gray-200" />
      </div>
      
      {/* Social Sign-Up at Bottom */}
      <div className="w-full mt-0">
        <button
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="w-full mb-4 px-4 py-3 border border-gray-300 rounded-xl text-base font-medium bg-white hover:bg-gray-50 transition-colors flex items-center justify-center space-x-3"
          style={{fontWeight: 600}}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span className="text-gray-700">Continue with Google</span>
        </button>
        <button
          disabled
          className="w-full px-4 py-3 border border-transparent rounded-xl text-base font-medium bg-black hover:bg-gray-900 transition-colors flex items-center justify-center space-x-3"
          style={{fontWeight: 600}}
        >
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          <span className="text-white">Continue with Apple</span>
        </button>
      </div>
      
      {/* Footer */}
      <div className="mt-8 text-center w-full">
        <p className="text-[#757575] text-base" style={{fontWeight: 400}}>
          Already have an account?{' '}
          <Link to="/signin" style={{ color: COLORS.PRIMARY_BROWN, fontWeight: 500 }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignUp; 